/**
 * A simple inquiry form for owners considering hiring the firm — sends one
 * email to the firm's configured contact inbox (`CompanySettings.contactEmail`,
 * the same address the listing detail page's "Contact us" mailto link uses)
 * through the existing `lib/mail.ts` transport. Deliberately not CLAUDE.md's
 * "Inquiry / tour request" funnel step for prospective tenants — no staff
 * queue, no database row, just a message — per the direct ask for something
 * simple. `POST /api/hire-inquiries` runs under its own 10/minute-per-IP
 * rate-limit scope (app.ts), the same posture every other unauthenticated
 * write on this site already uses.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { api, ApiError } from '../../lib/api.js';

interface InquiryFormState {
  name: string;
  email: string;
  phone: string;
  message: string;
  website: string;
}

const EMPTY_FORM: InquiryFormState = { name: '', email: '', phone: '', message: '', website: '' };

export function HirePropertyManagerInquiryForm() {
  const [values, setValues] = useState<InquiryFormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  function set<K extends keyof InquiryFormState>(key: K, value: InquiryFormState[K]) {
    setValues((prev) => ({ ...prev, [key]: value }));
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await api('/hire-inquiries', { method: 'POST', body: JSON.stringify(values) });
      setSent(true);
      setValues(EMPTY_FORM);
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Something went wrong sending your message. Please try again or call us.',
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (sent) {
    return (
      <div className="public__inquiry">
        <h2>Message sent.</h2>
        <p className="muted">
          Thanks for reaching out — a property manager will get back to you shortly. Prefer to talk
          now? Call or text the number above.
        </p>
      </div>
    );
  }

  return (
    <div className="public__inquiry">
      <h2>Ask us about managing your property</h2>
      <p className="muted">Tell us a little about your rental and we&apos;ll follow up.</p>

      <form className="public__inquiry-form" onSubmit={(event) => void onSubmit(event)}>
        {error ? <p className="alert alert--error">{error}</p> : null}

        {/* Honeypot: hidden from sighted and screen-reader users alike via
            CSS + aria-hidden. A real visitor never fills this in — a filled
            value means a bot did, and the service layer drops it silently. */}
        <label className="public__inquiry-honeypot" aria-hidden="true">
          Leave this field blank
          <input
            type="text"
            name="website"
            tabIndex={-1}
            autoComplete="off"
            value={values.website}
            onChange={(event) => set('website', event.target.value)}
          />
        </label>

        <div className="public__inquiry-row">
          <label className="public__inquiry-field">
            <span>Name</span>
            <input
              type="text"
              required
              value={values.name}
              onChange={(event) => set('name', event.target.value)}
            />
          </label>

          <label className="public__inquiry-field">
            <span>Email</span>
            <input
              type="email"
              required
              value={values.email}
              onChange={(event) => set('email', event.target.value)}
            />
          </label>

          <label className="public__inquiry-field">
            <span>Phone (optional)</span>
            <input
              type="tel"
              value={values.phone}
              onChange={(event) => set('phone', event.target.value)}
            />
          </label>
        </div>

        <label className="public__inquiry-field">
          <span>Tell us about your property (optional)</span>
          <textarea
            rows={4}
            value={values.message}
            onChange={(event) => set('message', event.target.value)}
          />
        </label>

        <button className="button" type="submit" disabled={submitting}>
          {submitting ? 'Sending…' : 'Send inquiry'}
        </button>
      </form>
    </div>
  );
}
